import { SButton, SDropdown, SIcon } from "@superwall/legacy-ui"
import { ReactNode } from "react"

type CopyConfigButtonProps<T> = {
  sources: T[]
  onCopy: (source: T) => void
  getSourceKey: (source: T) => string | number
  getSourceLabel: (source: T) => ReactNode
  getSourceSubtitle?: (source: T) => ReactNode
  label?: string
  isLoading?: boolean
  disabled?: boolean
  className?: string
}

export const CopyConfigButton = <T,>({
  sources,
  onCopy,
  getSourceKey,
  getSourceLabel,
  getSourceSubtitle,
  label = "Copy existing",
  isLoading = false,
  disabled = false,
  className,
}: CopyConfigButtonProps<T>) => {
  const buttonClassName = `${className ?? ""} whitespace-nowrap`.trim()

  if (isLoading) {
    return (
      <SButton variant="tertiary" size="sm" isLoading disabled className={buttonClassName}>
        {label}
      </SButton>
    )
  }

  if (disabled || sources.length === 0) {
    return (
      <SButton variant="tertiary" size="sm" disabled className={buttonClassName}>
        {label}
      </SButton>
    )
  }

  if (sources.length === 1) {
    return (
      <SButton
        variant="tertiary"
        size="sm"
        onClick={() => onCopy(sources[0]!)}
        className={buttonClassName}
      >
        <div className="flex items-center gap-2 whitespace-nowrap">
          <SIcon type="copy" size={14} />
          {label}
        </div>
      </SButton>
    )
  }

  return (
    <SDropdown
      isUnstyledItems
      width={280}
      buttonProps={{
        variant: "tertiary",
        size: "sm",
        className: buttonClassName,
      }}
      title={
        <div className="flex items-center gap-2 whitespace-nowrap">
          <SIcon type="copy" size={14} />
          <span>{label}</span>
          <SIcon type="chevron-down" size={10} className="opacity-40" />
        </div>
      }
      elements={sources.map((source) => {
        const subtitle = getSourceSubtitle?.(source)
        return (
          <SDropdown.Item
            key={getSourceKey(source)}
            onClick={() => onCopy(source)}
            $size="sm"
            className="flex flex-col items-start gap-1"
          >
            <span className="text-white">{getSourceLabel(source)}</span>
            {subtitle ? <span className="text-white/50 text-xs">{subtitle}</span> : null}
          </SDropdown.Item>
        )
      })}
    />
  )
}
